import { isPendingBecomeListener } from './listenerOnboarding';

/**
 * Console user-type ids vary (listener, provider, host, seller).
 * Treat those as the “offer listening” side of signup cards only.
 * Do not use this to infer an account role — Sharetribe’s default
 * author type is often `provider`, including for talker-only users.
 */
export const isListenerUserType = (userType, label = '') => {
  const haystack = `${userType || ''} ${label || ''}`.toLowerCase();
  return /(listen|provider|host|seller)/.test(haystack);
};

/**
 * True only when the Console type or label is explicitly Listen.
 * Generic provider / host / seller must not flip a talker to Listen.
 *
 * @param {string} [userType]
 * @param {string} [label]
 * @returns {boolean}
 */
export const isExplicitListenerUserType = (userType, label = '') => {
  const haystack = `${userType || ''} ${label || ''}`.toLowerCase();
  return /listen/.test(haystack);
};

export const listenerSignupUserType = (userTypes = []) => {
  const match = (userTypes || []).find(config =>
    isListenerUserType(config?.userType, config?.label)
  );
  return match?.userType || null;
};

export const LISTENER_SIGNUP_INTENT = 'listener';
export const LISTENER_PROFILE_CREATE_PATH = '/l/new';
const LISTENER_SETUP_STORAGE_KEY = 'slListenerSetup';

/**
 * Remember that an already-signed-in user started Listening-profile setup.
 * Signup/signin never uses this to skip account creation.
 */
export const saveListenerSetupIntent = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    window.sessionStorage.setItem(LISTENER_SETUP_STORAGE_KEY, '1');
  } catch (e) {
    // Private mode / blocked storage — keep going without persistence.
  }
};

export const peekListenerSetupIntent = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(LISTENER_SETUP_STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

export const clearListenerSetupIntent = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    window.sessionStorage.removeItem(LISTENER_SETUP_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

/**
 * True when this path is Listening-profile create/edit, not browse/book.
 */
export const isListenerProfileSetupPath = path => {
  if (typeof path !== 'string') {
    return false;
  }
  const pathname = path.split('?')[0].split('#')[0];
  if (pathname === '/l/new' || pathname === LISTENER_PROFILE_CREATE_PATH) {
    return true;
  }
  return /^\/l\/[^/]+\/[^/]+\/(new|draft|edit)(\/|$)/.test(pathname);
};

/**
 * Signup/signin/verify never auto-continues into Listening-profile setup.
 * Everyone becomes a user first; Become a listener is a later, optional step.
 */
export const shouldContinueListenerSetup = () => false;

/**
 * Logged-out: the same Sign up as talkers (email, password, name, TOS).
 * Logged-in without a profile: create a Listening profile.
 * Logged-in with a profile: manage that profile.
 *
 * @param {Object} [params]
 * @param {Array} [params.userTypes]
 * @param {boolean} [params.isAuthenticated]
 * @param {boolean} [params.currentUserHasListings]
 * @returns {{ name: string, params?: Object, to?: Object }}
 */
export const listenerCtaDestination = ({
  isAuthenticated = false,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  alreadyListener = false,
} = {}) => {
  if (isAuthenticated && (currentUserHasListings || currentUserOwnListing || alreadyListener)) {
    return { name: 'ManageListingsPage' };
  }
  if (isAuthenticated) {
    saveListenerSetupIntent();
    return { name: 'NewListingPage' };
  }
  return { name: 'SignupPage' };
};

/**
 * @param {{ isAuthenticated?: boolean, currentUserHasListings?: boolean }} [params]
 * @returns {string}
 */
export const listenerCtaMessageId = ({
  isAuthenticated = false,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  alreadyListener = false,
} = {}) => {
  if (isAuthenticated && (currentUserHasListings || currentUserOwnListing || alreadyListener)) {
    return 'TopbarDesktop.yourListingsLink';
  }
  if (isAuthenticated) {
    return 'ManageListingsPage.createListing';
  }
  return 'ListenerKeepShare.signup';
};

/**
 * True when this signup URL asked for the listener path.
 *
 * @param {Object} [params]
 * @param {string} [params.userType]
 * @param {string} [params.search]
 * @returns {boolean}
 */
export const isListenerSignupIntent = ({ userType, search } = {}) => {
  if (isListenerUserType(userType)) {
    return true;
  }
  const raw = typeof search === 'string' ? search.replace(/^\?/, '') : '';
  return raw.split('&').some(part => {
    const [key, value] = part.split('=');
    return key === 'intent' && value === LISTENER_SIGNUP_INTENT;
  });
};

export const talkerSignupUserType = (userTypes = []) => {
  const match = (userTypes || []).find(
    config => !isListenerUserType(config?.userType, config?.label)
  );
  return match?.userType || null;
};

export const userTypeCardLabelId = (userType, label = '') =>
  isListenerUserType(userType, label)
    ? 'FieldSelectUserType.optionListen'
    : 'FieldSelectUserType.optionTalk';

const accountRoleFromUser = currentUser => {
  const profile = currentUser?.attributes?.profile || {};
  const pick = data => {
    const role = data?.accountRole;
    return role === 'talk' || role === 'listen' || role === 'both' ? role : null;
  };
  const saved =
    pick(profile.publicData) || pick(profile.privateData) || pick(profile.protectedData);
  if (saved) {
    return saved;
  }
  const userType =
    profile.publicData?.userType ||
    profile.privateData?.userType ||
    profile.protectedData?.userType;
  return isExplicitListenerUserType(userType) ? 'listen' : null;
};

/**
 * Talkers who are not listeners should not get a primary “Listening profile”
 * nav item that opens an empty manage page. Listen / Both always get it,
 * including Listen signup userType before a profile exists.
 */
export const showListeningProfileNav = ({
  currentUser,
  currentUserHasListings,
  currentUserOwnListing,
  ownListings = [],
  currentUserHasListingsFetched,
} = {}) => {
  if (
    isPendingBecomeListener({
      currentUser,
      currentUserHasListings,
      currentUserHasListingsFetched,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return false;
  }
  const role = accountRoleFromUser(currentUser);
  if (role === 'talk') {
    return false;
  }
  if (role === 'listen' || role === 'both') {
    return true;
  }
  return false;
};

/**
 * Listener dashboard / Available now: published profiles, or a listener user type.
 * Do not require the hasListings flag alone — that query can lag or 429.
 */
export const isListenerDashboardUser = ({
  currentUser,
  currentUserHasListings,
  currentUserOwnListing = null,
  ownListings = [],
  currentUserHasListingsFetched,
} = {}) => {
  if (
    isPendingBecomeListener({
      currentUser,
      currentUserHasListings,
      currentUserHasListingsFetched,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return false;
  }
  const role = accountRoleFromUser(currentUser);
  if (role === 'talk') {
    return false;
  }
  if (role === 'listen' || role === 'both') {
    return true;
  }
  return showListeningProfileNav({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
};
